import { Modal, Button } from '@platform/ui';
import { SEOUL_TZ } from '@platform/datetime';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { WithdrawalPreview } from '../api/subscription.api';

interface Props {
  open: boolean;
  preview: WithdrawalPreview | null;
  planName: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const md = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  month: 'long',
  day: 'numeric',
});

function fmt(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : md.format(d);
}

/**
 * 불가 사유별 안내.
 *
 * 사유마다 **다음에 할 일이 다르다** — 해지 신청으로 갈지, 고객센터로 갈지,
 * 아무것도 못 하는지. 하나로 뭉뚱그리면 그 판단을 사용자가 못 한다.
 */
function ineligibleMessage(p: WithdrawalPreview): string {
  switch (p.reason) {
    case 'ALREADY_USED': {
      // 활성화 일시가 없을 수 있다 — 날짜 없이도 문장이 성립해야 한다('-' 를 찍지 않는다).
      const when = p.activatedAt ? `${fmt(p.activatedAt)} ` : '';
      const device = p.activatedDeviceName ? `(${p.activatedDeviceName}) ` : '';
      return `${when}${device}라이선스 활성화로 청약철회가 제한됩니다. 해지를 원하시면 해지 신청을 이용해주세요.`;
    }
    case 'WINDOW_EXPIRED':
      return '청약철회 가능 기간(결제 후 7일)이 지났습니다. 해지 신청을 이용해주세요.';
    case 'NO_REFUNDABLE_PAYMENT':
      return '환불할 결제 내역이 없습니다. 구독 종료는 해지 신청으로 진행해주세요.';
    case 'TERMINATED':
      return '이미 종료된 구독입니다.';
    case 'ORG_SUBSCRIPTION':
      return '기업 구독은 고객센터를 통해 처리됩니다.';
    default:
      return '지금은 청약철회를 진행할 수 없습니다. 고객센터로 문의해주세요.';
  }
}

/**
 * 청약철회 확인 다이얼로그.
 *
 * 되돌릴 수 없는 동작이라 **환불 예정액과 즉시 종료**를 확인 화면에 명시한다.
 * 금액은 서버 프리뷰 값을 그대로 쓴다 — 화면에서 계산하면 실제 환불액과 갈라진다.
 */
export function WithdrawalDialog({
  open,
  preview,
  planName,
  busy,
  onClose,
  onConfirm,
}: Props) {
  const eligible = preview?.eligible === true;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="청약철회"
      description="결제를 취소하고 구독을 즉시 종료합니다."
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!eligible || busy}
          >
            {busy ? '처리 중…' : '청약철회하고 환불받기'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-gray-500">환불 예정 금액</span>
            <span className="text-lg font-bold text-gray-900">
              {won(preview?.refundAmount ?? 0)}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {planName}
            {/* 마감일을 같이 준다 — "언제까지 가능한지"를 모르면 미루다 권리를 잃는다. */}
            {preview && preview.withinWindow && (
              <> · {fmt(preview.deadline)}까지 신청 가능</>
            )}
          </p>
        </div>

        {/* 되돌릴 수 없다는 사실을 결과와 함께 적는다. 버튼 문구만으로는 부족하다. */}
        <ul className="space-y-1 text-xs text-gray-600">
          <li>· 결제하신 금액 전액이 결제수단으로 환불됩니다.</li>
          <li>
            · 구독과 라이선스가 <b>즉시 종료</b>되며 서비스를 이용할 수
            없습니다.
          </li>
          <li>
            · 카드사 정책에 따라 취소 반영까지 영업일 3~5일이 걸릴 수 있습니다.
          </li>
          <li>
            · 이 작업은 되돌릴 수 없습니다. 다시 이용하려면 새로 구독해야
            합니다.
          </li>
        </ul>

        {preview && !preview.eligible && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {ineligibleMessage(preview)}
          </p>
        )}

        <p className="text-xs text-gray-400">
          자세한 기준은{' '}
          <a
            href={PATHS.LEGAL_REFUND}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline underline-offset-2"
          >
            환불·해지 정책
          </a>
          에서 확인할 수 있습니다.
        </p>
      </div>
    </Modal>
  );
}
